import { formatDate } from '@/formatters/formatDate';
import type {
  GetContactProfileAttributesOrderQuery,
  GetContactQuery,
} from '@/generated/usermobile-bff/graphql';

interface TranslationOptions {
  readonly defaultValue?: string;
  readonly [key: string]: unknown;
}

interface TranslationFunction {
  (key: string, options?: TranslationOptions): string;
}

interface ContactProfileRow {
  readonly key: string;
  readonly label: string;
  readonly value: string | null;
}

interface ResolverContext {
  readonly contact: NonNullable<GetContactQuery['contact']>;
  readonly contactStatusNames: ReadonlyMap<number, string>;
  readonly t: TranslationFunction;
  readonly locale: string;
}

interface ResolveContactProfileRowsArgs extends ResolverContext {
  readonly preferences: GetContactProfileAttributesOrderQuery['contactProfileAttributesOrder'];
}

interface StaticFieldDefinition {
  readonly labelKey: string;
  readonly getValue: (context: ResolverContext) => string | null;
}

interface InquiryFieldDefinition {
  readonly labelKey: string;
  readonly getValue: (context: ResolverContext) => string | null;
}

const hasResolvedValue = (value: string | null): value is string => value !== null;

const normalizeText = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return null;
  }

  return trimmed;
};

const formatNumericValue = (value: number | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  return String(value);
};

const formatDateValue = (value: string | null | undefined, locale: string): string | null => {
  const normalizedValue = normalizeText(value);

  if (!normalizedValue) {
    return null;
  }

  const timestamp = Date.parse(normalizedValue);

  if (Number.isNaN(timestamp)) {
    return normalizedValue;
  }

  return formatDate(normalizedValue, locale);
};

const formatJoinedValues = (values: readonly string[]): string | null => {
  if (values.length === 0) {
    return null;
  }

  return values.join(', ');
};

const resolveContactStatusName = (
  contactStatusId: number | null | undefined,
  contactStatusNames: ReadonlyMap<number, string>,
): string | null => {
  if (contactStatusId === null || contactStatusId === undefined) {
    return null;
  }

  return normalizeText(contactStatusNames.get(contactStatusId));
};

const translateGender = (
  value: string | null | undefined,
  t: TranslationFunction,
): string | null => {
  const normalizedValue = normalizeText(value);

  if (!normalizedValue) {
    return null;
  }

  switch (normalizedValue) {
    case 'male':
      return t('contacts.profile.attributeValues.gender.male');
    case 'female':
      return t('contacts.profile.attributeValues.gender.female');
    case 'other':
      return t('contacts.profile.attributeValues.gender.other');
    default:
      return t('contacts.profile.attributeValues.gender.notSpecified');
  }
};

const translateCreationMethod = (
  value: string | null | undefined,
  t: TranslationFunction,
): string | null => {
  const normalizedValue = normalizeText(value);

  if (!normalizedValue) {
    return null;
  }

  switch (normalizedValue) {
    case 'api':
      return t('contacts.profile.attributeValues.creationMethod.api');
    case 'import':
      return t('contacts.profile.attributeValues.creationMethod.import');
    case 'web_form':
      return t('contacts.profile.attributeValues.creationMethod.leadAcquisition');
    case 'web_interface':
      return t('contacts.profile.attributeValues.creationMethod.webInterface');
    case 'lead_acquisition':
      return t('contacts.profile.attributeValues.creationMethod.leadAcquisition');
    case 'sms_inbound':
      return t('contacts.profile.attributeValues.creationMethod.smsReceived');
    case 'line':
      return t('contacts.profile.attributeValues.creationMethod.lineFriends');
    case 'andpad':
      return t('contacts.profile.attributeValues.creationMethod.andpad');
    case 'kintone':
      return t('contacts.profile.attributeValues.creationMethod.kintone');
    case 'sent_from_franchisor':
      return t('contacts.profile.attributeValues.creationMethod.sentFromFranchisor');
    default:
      return t('contacts.profile.attributeValues.creationMethod.unknown');
  }
};

const translateLastActionType = (
  value: string | null | undefined,
  t: TranslationFunction,
): string | null => {
  const normalizedValue = normalizeText(value);

  if (!normalizedValue) {
    return null;
  }

  switch (normalizedValue) {
    case 'contact_email_clicked':
    case 'contact_email_inbox_clicked':
      return t('contacts.profile.attributeValues.lastActionType.emailClicked');
    case 'contact_email_opened':
    case 'contact_email_inbox_opened':
      return t('contacts.profile.attributeValues.lastActionType.emailOpened');
    case 'contact_email_inbox_received':
      return t('contacts.profile.attributeValues.lastActionType.emailReceived');
    case 'contact_email_inbox_sent':
      return t('contacts.profile.attributeValues.lastActionType.emailSent');
    case 'contact_web_form_submitted':
    case 'contact_lead_acquisition_submitted':
    case 'lead_acquisition':
      return t('contacts.profile.attributeValues.lastActionType.leadAcquisition');
    case 'contact_page_visited':
      return t('contacts.profile.attributeValues.lastActionType.pageVisited');
    case 'contact_email_unsubscribed':
      return t('contacts.profile.attributeValues.lastActionType.emailUnsubscribed');
    case 'contact_line_clicked':
      return t('contacts.profile.attributeValues.lastActionType.lineClicked');
    case 'contact_sms_link_clicked':
      return t('contacts.profile.attributeValues.lastActionType.smsClicked');
    case 'contact_sms_message_received':
    case 'contact_sms_inbound_message_received':
      return t('contacts.profile.attributeValues.lastActionType.smsReceived');
    case 'contact_line_channel_subscribed':
      return t('contacts.profile.attributeValues.lastActionType.lineFriendAdded');
    case 'contact_line_inbound_message_received':
      return t('contacts.profile.attributeValues.lastActionType.lineMessageReceived');
    case 'contact_line_outbound_message_sent':
      return t('contacts.profile.attributeValues.lastActionType.lineMessageSent');
    default:
      return t('contacts.profile.attributeValues.lastActionType.unknown');
  }
};

const staticFieldDefinitions: Record<string, StaticFieldDefinition> = {
  id: {
    labelKey: 'contacts.profile.attributeLabels.id',
    getValue: ({ contact }) => formatNumericValue(contact.id),
  },
  company_id: {
    labelKey: 'contacts.profile.attributeLabels.companyId',
    getValue: ({ contact }) => formatNumericValue(contact.companyId),
  },
  uid: {
    labelKey: 'contacts.profile.attributeLabels.uid',
    getValue: ({ contact }) => normalizeText(contact.uid),
  },
  contact_uid: {
    labelKey: 'contacts.profile.attributeLabels.uid',
    getValue: ({ contact }) => normalizeText(contact.uid),
  },
  first_name: {
    labelKey: 'contacts.profile.attributeLabels.firstName',
    getValue: ({ contact }) => normalizeText(contact.firstName),
  },
  first_name_kana: {
    labelKey: 'contacts.profile.attributeLabels.firstNameKana',
    getValue: ({ contact }) => normalizeText(contact.firstNameKana),
  },
  last_name: {
    labelKey: 'contacts.profile.attributeLabels.lastName',
    getValue: ({ contact }) => normalizeText(contact.lastName),
  },
  last_name_kana: {
    labelKey: 'contacts.profile.attributeLabels.lastNameKana',
    getValue: ({ contact }) => normalizeText(contact.lastNameKana),
  },
  full_name: {
    labelKey: 'contacts.profile.attributeLabels.fullName',
    getValue: ({ contact }) => normalizeText(contact.fullName),
  },
  email: {
    labelKey: 'contacts.profile.attributeLabels.email',
    getValue: ({ contact }) => normalizeText(contact.email),
  },
  age: {
    labelKey: 'contacts.profile.attributeLabels.age',
    getValue: ({ contact }) => normalizeText(contact.age),
  },
  gender: {
    labelKey: 'contacts.profile.attributeLabels.gender',
    getValue: ({ contact, t }) => translateGender(contact.gender, t),
  },
  personal_mobile_number: {
    labelKey: 'contacts.profile.attributeLabels.personalMobileNumber',
    getValue: ({ contact }) => normalizeText(contact.personalMobileNumber),
  },
  personal_mobile_number_country_code: {
    labelKey: 'contacts.profile.attributeLabels.personalMobileNumberCountryCode',
    getValue: ({ contact }) => normalizeText(contact.personalMobileNumberCountryCode),
  },
  personal_landline_number: {
    labelKey: 'contacts.profile.attributeLabels.personalLandlineNumber',
    getValue: ({ contact }) => normalizeText(contact.personalLandlineNumber),
  },
  personal_landline_number_country_code: {
    labelKey: 'contacts.profile.attributeLabels.personalLandlineNumberCountryCode',
    getValue: ({ contact }) => normalizeText(contact.personalLandlineNumberCountryCode),
  },
  personal_fax_number: {
    labelKey: 'contacts.profile.attributeLabels.personalFaxNumber',
    getValue: ({ contact }) => normalizeText(contact.personalFaxNumber),
  },
  personal_fax_number_country_code: {
    labelKey: 'contacts.profile.attributeLabels.personalFaxNumberCountryCode',
    getValue: ({ contact }) => normalizeText(contact.personalFaxNumberCountryCode),
  },
  personal_country: {
    labelKey: 'contacts.profile.attributeLabels.personalCountry',
    getValue: ({ contact }) => normalizeText(contact.personalCountry),
  },
  personal_region: {
    labelKey: 'contacts.profile.attributeLabels.personalRegion',
    getValue: ({ contact }) => normalizeText(contact.personalRegion),
  },
  personal_city: {
    labelKey: 'contacts.profile.attributeLabels.personalCity',
    getValue: ({ contact }) => normalizeText(contact.personalCity),
  },
  personal_postal_code: {
    labelKey: 'contacts.profile.attributeLabels.personalPostalCode',
    getValue: ({ contact }) => normalizeText(contact.personalPostalCode),
  },
  personal_address_1: {
    labelKey: 'contacts.profile.attributeLabels.personalAddress1',
    getValue: ({ contact }) => normalizeText(contact.personalAddress1),
  },
  personal_address_2: {
    labelKey: 'contacts.profile.attributeLabels.personalAddress2',
    getValue: ({ contact }) => normalizeText(contact.personalAddress2),
  },
  personal_website_url: {
    labelKey: 'contacts.profile.attributeLabels.personalWebsiteUrl',
    getValue: ({ contact }) => normalizeText(contact.personalWebsiteUrl),
  },
  work_company_name: {
    labelKey: 'contacts.profile.attributeLabels.workCompanyName',
    getValue: ({ contact }) => normalizeText(contact.workCompanyName),
  },
  work_website_url: {
    labelKey: 'contacts.profile.attributeLabels.workWebsiteUrl',
    getValue: ({ contact }) => normalizeText(contact.workWebsiteUrl),
  },
  work_department: {
    labelKey: 'contacts.profile.attributeLabels.workDepartment',
    getValue: ({ contact }) => normalizeText(contact.workDepartment),
  },
  work_job_title: {
    labelKey: 'contacts.profile.attributeLabels.workJobTitle',
    getValue: ({ contact }) => normalizeText(contact.workJobTitle),
  },
  work_landline_number: {
    labelKey: 'contacts.profile.attributeLabels.workLandlineNumber',
    getValue: ({ contact }) => normalizeText(contact.workLandlineNumber),
  },
  work_landline_number_country_code: {
    labelKey: 'contacts.profile.attributeLabels.workLandlineNumberCountryCode',
    getValue: ({ contact }) => normalizeText(contact.workLandlineNumberCountryCode),
  },
  work_mobile_number: {
    labelKey: 'contacts.profile.attributeLabels.workMobileNumber',
    getValue: ({ contact }) => normalizeText(contact.workMobileNumber),
  },
  work_mobile_number_country_code: {
    labelKey: 'contacts.profile.attributeLabels.workMobileNumberCountryCode',
    getValue: ({ contact }) => normalizeText(contact.workMobileNumberCountryCode),
  },
  work_fax_number: {
    labelKey: 'contacts.profile.attributeLabels.workFaxNumber',
    getValue: ({ contact }) => normalizeText(contact.workFaxNumber),
  },
  work_fax_number_country_code: {
    labelKey: 'contacts.profile.attributeLabels.workFaxNumberCountryCode',
    getValue: ({ contact }) => normalizeText(contact.workFaxNumberCountryCode),
  },
  work_country: {
    labelKey: 'contacts.profile.attributeLabels.workCountry',
    getValue: ({ contact }) => normalizeText(contact.workCountry),
  },
  work_region: {
    labelKey: 'contacts.profile.attributeLabels.workRegion',
    getValue: ({ contact }) => normalizeText(contact.workRegion),
  },
  work_city: {
    labelKey: 'contacts.profile.attributeLabels.workCity',
    getValue: ({ contact }) => normalizeText(contact.workCity),
  },
  work_postal_code: {
    labelKey: 'contacts.profile.attributeLabels.workPostalCode',
    getValue: ({ contact }) => normalizeText(contact.workPostalCode),
  },
  work_address_1: {
    labelKey: 'contacts.profile.attributeLabels.workAddress1',
    getValue: ({ contact }) => normalizeText(contact.workAddress1),
  },
  work_address_2: {
    labelKey: 'contacts.profile.attributeLabels.workAddress2',
    getValue: ({ contact }) => normalizeText(contact.workAddress2),
  },
  contact_status_id: {
    labelKey: 'contacts.profile.attributeLabels.contactStatusId',
    getValue: ({ contact, contactStatusNames }) =>
      resolveContactStatusName(contact.contactStatusId, contactStatusNames),
  },
  status: {
    labelKey: 'contacts.profile.attributeLabels.contactStatusId',
    getValue: ({ contact, contactStatusNames }) =>
      resolveContactStatusName(contact.contactStatusId, contactStatusNames),
  },
  person_in_charge_id: {
    labelKey: 'contacts.profile.attributeLabels.personInChargeId',
    getValue: ({ contact }) => normalizeText(contact.personInCharge?.name),
  },
  person_in_charge: {
    labelKey: 'contacts.profile.attributeLabels.personInChargeId',
    getValue: ({ contact }) => normalizeText(contact.personInCharge?.name),
  },
  notes: {
    labelKey: 'contacts.profile.attributeLabels.notes',
    getValue: ({ contact }) => normalizeText(contact.notes),
  },
  first_approached_at: {
    labelKey: 'contacts.profile.attributeLabels.firstApproachedAt',
    getValue: ({ contact, locale }) => formatDateValue(contact.firstApproachedAt, locale),
  },
  last_approached_at: {
    labelKey: 'contacts.profile.attributeLabels.lastApproachedAt',
    getValue: ({ contact, locale }) => formatDateValue(contact.lastApproachedAt, locale),
  },
  created_at: {
    labelKey: 'contacts.profile.attributeLabels.createdAt',
    getValue: ({ contact, locale }) => formatDateValue(contact.createdAt, locale),
  },
  updated_at: {
    labelKey: 'contacts.profile.attributeLabels.updatedAt',
    getValue: ({ contact, locale }) => formatDateValue(contact.updatedAt, locale),
  },
  archived_at: {
    labelKey: 'contacts.profile.attributeLabels.archivedAt',
    getValue: ({ contact, locale }) => formatDateValue(contact.archivedAt, locale),
  },
  last_action_occurred_at: {
    labelKey: 'contacts.profile.attributeLabels.lastActionOccurredAt',
    getValue: ({ contact, locale }) => formatDateValue(contact.lastActionOccurredAt, locale),
  },
  creation_method: {
    labelKey: 'contacts.profile.attributeLabels.creationMethod',
    getValue: ({ contact, t }) => translateCreationMethod(contact.creationMethod, t),
  },
  last_action_type: {
    labelKey: 'contacts.profile.attributeLabels.lastActionType',
    getValue: ({ contact, t }) => translateLastActionType(contact.lastActionType, t),
  },
  customer_type: {
    labelKey: 'contacts.profile.attributeLabels.customerType',
    getValue: ({ contact }) => formatJoinedValues(contact.customerTypeNames),
  },
  lead_acquisition_channels: {
    labelKey: 'contacts.profile.attributeLabels.leadAcquisitionChannels',
    getValue: ({ contact }) => formatJoinedValues(contact.leadAcquisitionChannelNames),
  },
};

const realEstateInquiryDefinitions: Record<string, InquiryFieldDefinition> = {
  client_selected_plan: {
    labelKey: 'contacts.profile.attributeLabels.realEstateInquiry.clientSelectedPlan',
    getValue: ({ contact }) => normalizeText(contact.realEstateInquiry?.clientSelectedPlan),
  },
  inquired_at: {
    labelKey: 'contacts.profile.attributeLabels.realEstateInquiry.inquiredAt',
    getValue: ({ contact, locale }) =>
      formatDateValue(contact.realEstateInquiry?.inquiredAt, locale),
  },
  preferred_mode_of_communication: {
    labelKey: 'contacts.profile.attributeLabels.realEstateInquiry.preferredModeOfCommunication',
    getValue: ({ contact }) =>
      normalizeText(contact.realEstateInquiry?.preferredModeOfCommunication),
  },
  preferred_time_of_communication: {
    labelKey: 'contacts.profile.attributeLabels.realEstateInquiry.preferredTimeOfCommunication',
    getValue: ({ contact }) =>
      normalizeText(contact.realEstateInquiry?.preferredTimeOfCommunication),
  },
  content: {
    labelKey: 'contacts.profile.attributeLabels.realEstateInquiry.content',
    getValue: ({ contact }) => normalizeText(contact.realEstateInquiry?.content),
  },
  yearly_income: {
    labelKey: 'contacts.profile.attributeLabels.realEstateInquiry.yearlyIncome',
    getValue: ({ contact }) => normalizeText(contact.realEstateInquiry?.yearlyIncome),
  },
};

const realEstateConstructionInquiryDefinitions: Record<string, InquiryFieldDefinition> = {
  location_details: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.locationDetails',
    getValue: ({ contact }) =>
      normalizeText(contact.realEstateConstructionInquiry?.locationDetails),
  },
  planned_timing: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.plannedTiming',
    getValue: ({ contact }) => normalizeText(contact.realEstateConstructionInquiry?.plannedTiming),
  },
  budget: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.budget',
    getValue: ({ contact }) => normalizeText(contact.realEstateConstructionInquiry?.budget),
  },
  requirements: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.requirements',
    getValue: ({ contact }) => normalizeText(contact.realEstateConstructionInquiry?.requirements),
  },
  type: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.type',
    getValue: ({ contact }) => normalizeText(contact.realEstateConstructionInquiry?.type),
  },
  model_house_name: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.modelHouseName',
    getValue: ({ contact }) => normalizeText(contact.realEstateConstructionInquiry?.modelHouseName),
  },
  reservation_timing: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.reservationTiming',
    getValue: ({ contact, locale }) =>
      formatDateValue(contact.realEstateConstructionInquiry?.reservationTiming, locale),
  },
  event_name: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.eventName',
    getValue: ({ contact }) => normalizeText(contact.realEstateConstructionInquiry?.eventName),
  },
  event_timing: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.eventTiming',
    getValue: ({ contact, locale }) =>
      formatDateValue(contact.realEstateConstructionInquiry?.eventTiming, locale),
  },
  preferred_catalog_name: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.preferredCatalogName',
    getValue: ({ contact }) =>
      normalizeText(contact.realEstateConstructionInquiry?.preferredCatalogName),
  },
  exhibition_venue_name: {
    labelKey: 'contacts.profile.attributeLabels.realEstateConstructionInquiry.exhibitionVenueName',
    getValue: ({ contact }) =>
      normalizeText(contact.realEstateConstructionInquiry?.exhibitionVenueName),
  },
};

const realEstatePurchaseInquiryDefinitions: Record<string, InquiryFieldDefinition> = {
  property_type: {
    labelKey: 'contacts.profile.attributeLabels.realEstatePurchaseInquiry.propertyType',
    getValue: ({ contact }) => normalizeText(contact.realEstatePurchaseInquiry?.propertyType),
  },
  property_name: {
    labelKey: 'contacts.profile.attributeLabels.realEstatePurchaseInquiry.propertyName',
    getValue: ({ contact }) => normalizeText(contact.realEstatePurchaseInquiry?.propertyName),
  },
  preferred_move_in_date: {
    labelKey: 'contacts.profile.attributeLabels.realEstatePurchaseInquiry.preferredMoveInDate',
    getValue: ({ contact, locale }) =>
      formatDateValue(contact.realEstatePurchaseInquiry?.preferredMoveInDate, locale),
  },
  property_image_url: {
    labelKey: 'contacts.profile.attributeLabels.realEstatePurchaseInquiry.propertyImageUrl',
    getValue: ({ contact }) => normalizeText(contact.realEstatePurchaseInquiry?.propertyImageUrl),
  },
  preferred_property_specification: {
    labelKey:
      'contacts.profile.attributeLabels.realEstatePurchaseInquiry.preferredPropertySpecification',
    getValue: ({ contact }) =>
      normalizeText(contact.realEstatePurchaseInquiry?.preferredPropertySpecification),
  },
  preferred_area: {
    labelKey: 'contacts.profile.attributeLabels.realEstatePurchaseInquiry.preferredArea',
    getValue: ({ contact }) => normalizeText(contact.realEstatePurchaseInquiry?.preferredArea),
  },
};

const realEstateSaleInquiryDefinitions: Record<string, InquiryFieldDefinition> = {
  property_type: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.propertyType',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.propertyType),
  },
  property_address: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.propertyAddress',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.propertyAddress),
  },
  property_land_area: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.propertyLandArea',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.propertyLandArea),
  },
  property_building_area: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.propertyBuildingArea',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.propertyBuildingArea),
  },
  property_floor_plan: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.propertyFloorPlan',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.propertyFloorPlan),
  },
  property_age: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.propertyAge',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.propertyAge),
  },
  property_vacancy_status: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.propertyVacancyStatus',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.propertyVacancyStatus),
  },
  property_owner_name: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.propertyOwnerName',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.propertyOwnerName),
  },
  preferred_sale_time: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.preferredSaleTime',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.preferredSaleTime),
  },
  sale_reason: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.saleReason',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.saleReason),
  },
  preferred_assessment_method: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.preferredAssessmentMethod',
    getValue: ({ contact }) =>
      normalizeText(contact.realEstateSaleInquiry?.preferredAssessmentMethod),
  },
  offer_price: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.offerPrice',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.offerPrice),
  },
  requests: {
    labelKey: 'contacts.profile.attributeLabels.realEstateSaleInquiry.requests',
    getValue: ({ contact }) => normalizeText(contact.realEstateSaleInquiry?.requests),
  },
};

const resolveStaticField = (
  preference: GetContactProfileAttributesOrderQuery['contactProfileAttributesOrder'][number],
  context: ResolverContext,
): ContactProfileRow | null => {
  const definition = staticFieldDefinitions[preference.name];

  if (!definition) {
    return null;
  }

  return {
    key: `static:${preference.name}`,
    label: context.t(definition.labelKey),
    value: definition.getValue(context),
  };
};

const resolveCustomField = (
  preference: GetContactProfileAttributesOrderQuery['contactProfileAttributesOrder'][number],
  context: ResolverContext,
): ContactProfileRow | null => {
  const customField = context.contact.customFields.find((field) => field.code === preference.name);

  if (!customField) {
    return null;
  }

  return {
    key: `custom:${preference.name}`,
    label: normalizeText(preference.label) ?? customField.label,
    value: normalizeText(customField.value),
  };
};

const resolveInquiryField = (
  group: string,
  preference: GetContactProfileAttributesOrderQuery['contactProfileAttributesOrder'][number],
  context: ResolverContext,
): ContactProfileRow | null => {
  const definitionMaps: Record<string, Record<string, InquiryFieldDefinition>> = {
    real_estate_inquiry: realEstateInquiryDefinitions,
    real_estate_construction_inquiry: realEstateConstructionInquiryDefinitions,
    real_estate_purchase_inquiry: realEstatePurchaseInquiryDefinitions,
    real_estate_sale_inquiry: realEstateSaleInquiryDefinitions,
  };
  const definition = definitionMaps[group]?.[preference.name];

  if (!definition) {
    return null;
  }

  return {
    key: `${group}:${preference.name}`,
    label: context.t(definition.labelKey),
    value: definition.getValue(context),
  };
};

const resolvePreferenceRow = (
  preference: GetContactProfileAttributesOrderQuery['contactProfileAttributesOrder'][number],
  context: ResolverContext,
): ContactProfileRow | null => {
  switch (preference.field) {
    case 'static':
      return resolveStaticField(preference, context);
    case 'custom':
      return resolveCustomField(preference, context);
    case 'real_estate_inquiry':
    case 'real_estate_construction_inquiry':
    case 'real_estate_purchase_inquiry':
    case 'real_estate_sale_inquiry':
      return resolveInquiryField(preference.field, preference, context);
    case 'andpad':
    case 'franchise':
    case 'external_sync_kintone':
      return null;
    default:
      return null;
  }
};

const resolveContactProfileRows = ({
  contact,
  contactStatusNames,
  preferences,
  t,
  locale,
}: ResolveContactProfileRowsArgs): ContactProfileRow[] =>
  preferences.flatMap((preference, index) => {
    const resolvedRow = resolvePreferenceRow(preference, {
      contact,
      contactStatusNames,
      t,
      locale,
    });

    if (!resolvedRow) {
      return [];
    }

    if (!hasResolvedValue(resolvedRow.value)) {
      return [];
    }

    return [{ ...resolvedRow, key: `${resolvedRow.key}:${index}` }];
  });

export { resolveContactProfileRows };
export type { ContactProfileRow };
